import React, { useEffect, useState, useCallback } from 'react';
import { Star } from 'lucide-react';
import * as api from '../../lib/api';
import Pagination from '../../components/Pagination';
import { TableCard, Th, Td, trClass, TableMessage, UserCell, fmtDateTime, StickyPager } from './ui';

const LIMIT = 25;

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [averageRating, setAverageRating] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.getAdminFeedback({ page: p, limit: LIMIT });
      setItems(res.items);
      setTotal(res.total);
      setAverageRating(res.average_rating);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  return (
    <div className="space-y-6 pb-2" data-testid="admin-feedback">
      {/* Hero — the one number that matters here */}
      <div>
        <p className="text-[13px] font-medium text-slate-500">Calificación promedio</p>
        <div className="mt-1 flex items-end gap-4">
          <p className="text-5xl sm:text-6xl font-semibold leading-none tracking-[-0.03em] text-white tabular-nums">{averageRating ?? '—'}</p>
          <div className="pb-1.5">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-4 h-4 ${averageRating && n <= Math.round(averageRating) ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">{total} respuesta{total === 1 ? '' : 's'}</p>
          </div>
        </div>
      </div>

      <TableCard>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <Th>Usuario</Th>
              <Th>Calificación</Th>
              <Th>Comentario</Th>
              <Th>Fecha</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableMessage colSpan={4}>Cargando…</TableMessage>
            ) : items.length === 0 ? (
              <TableMessage colSpan={4}>Sin respuestas todavía.</TableMessage>
            ) : items.map((f) => (
              <tr key={f.user_id} className={`${trClass} align-top`}>
                <Td className="max-w-[14rem]"><UserCell email={f.user_email} name={f.user_name} userId={f.user_id} /></Td>
                <Td>
                  <div className="flex items-center gap-0.5 pt-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= f.feedback_rating ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
                    ))}
                  </div>
                </Td>
                <Td className="text-slate-300 max-w-md leading-relaxed">{f.feedback_text || <span className="text-slate-600">—</span>}</Td>
                <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(f.feedback_submitted_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      <StickyPager show={total > LIMIT}>
        <Pagination page={page} pageCount={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={setPage} total={total} pageSize={LIMIT} />
      </StickyPager>
    </div>
  );
}
